import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Award, Gauge, CalendarCheck } from 'lucide-react';

const heroImages = [
  { src: "/hero-slide-1.png", alt: "Elektroanschlüsse und Stromkästen als Beispiel für Elektroinstallation", position: "center" },
  { src: "/hero-slide-2.jpeg", alt: "Beleuchtetes Firmenschild der K.H. und B. Daniels Elektroinstallationen GmbH", position: "center" },
  { src: "/hero-slide-3.jpeg", alt: "Sprechanlage beziehungsweise Klingelanlage installiert von Daniels Elektroinstallationen", position: "center" },
  { src: "/hero-slide-4.jpeg", alt: "Verteilerfeld beziehungsweise Elektroverteilung von Daniels Elektroinstallationen", position: "center 25%" }
];

const trustBadges = [
  { icon: Award, label: 'Meisterbetrieb seit 1966' },
  { icon: Gauge, label: 'Arbeiten nach DIN-VDE-Normen' },
  { icon: CalendarCheck, label: 'Termingerechte Ausführung' },
];

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  }, []);

  useEffect(() => {
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [nextSlide]);

  const scrollToKontakt = () => {
    const el = document.querySelector('#kontakt');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" data-testid="hero-section" className="relative h-screen min-h-[700px] overflow-hidden">
      {/* Background Images */}
      {heroImages.map((img, idx) => (
        <div
          key={idx}
          className="absolute inset-0 transition-opacity duration-[1500ms] ease-in-out"
          style={{ opacity: currentSlide === idx ? 1 : 0 }}
        >
          <img
            src={img.src}
            alt={img.alt}
            className="w-full h-full object-cover"
            style={{ objectPosition: img.position }}
          />
        </div>
      ))}

      {/* Green gradient overlay - left to right */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A22] via-[#1A3A22]/85 to-transparent z-10" />
      {/* Additional dark overlay for readability */}
      <div className="absolute inset-0 bg-black/20 z-10" />

      {/* Content */}
      <div className="relative z-20 h-full flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <span className="inline-block text-[#10B981] font-['Manrope'] text-sm sm:text-base font-semibold tracking-[0.2em] uppercase mb-4">
                Ihr Elektrofachbetrieb in Bonn
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="font-['Outfit'] text-4xl sm:text-5xl lg:text-7xl font-extrabold text-white tracking-tighter leading-[1.05] mb-6"
            >
              Elektroinstallationen
              <br />
              <span className="text-[#10B981]">Altbau | Neubau | Modernisierung</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="font-['Manrope'] text-white/80 text-base sm:text-lg leading-relaxed mb-8 max-w-lg"
            >
              Seit 1966 Ihr Elektriker in Bonn für professionelle Elektroinstallationen
              im Rhein-Sieg-Kreis und Raum Köln. Meisterbetrieb mit fairen Preisen und termingerechter Ausführung.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="flex flex-col sm:flex-row gap-4 mb-12"
            >
              <button
                onClick={scrollToKontakt}
                data-testid="hero-cta-button"
                className="bg-[#2D5A27] hover:bg-[#10B981] text-white px-8 py-4 font-['Outfit'] font-semibold text-base tracking-[0.1em] uppercase transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl border-2 border-[#2D5A27] hover:border-[#10B981]"
              >
                Jetzt Anfrage stellen
              </button>
              <a
                href="tel:+492282346 44"
                data-testid="hero-phone-button"
                className="border-2 border-white/30 hover:border-white text-white px-8 py-4 font-['Outfit'] font-semibold text-base tracking-[0.1em] uppercase transition-all duration-300 hover:-translate-y-1 text-center"
              >
                +49 228 23 46 44
              </a>
            </motion.div>

            {/* Trust badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.2 }}
              className="flex gap-6 sm:gap-8"
            >
              {trustBadges.map((badge, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <badge.icon className="w-5 h-5 text-[#10B981]" strokeWidth={2} />
                  <span className="text-white/70 font-['Manrope'] text-sm font-medium">{badge.label}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Slider Controls */}
      <div className="absolute bottom-8 right-8 z-20 flex items-center gap-3">
        <button
          onClick={prevSlide}
          data-testid="hero-prev-slide"
          className="w-12 h-12 flex items-center justify-center border border-white/30 hover:border-white text-white transition-all duration-300 hover:bg-white/10"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex gap-2">
          {heroImages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              data-testid={`hero-slide-dot-${idx}`}
              className={`h-1.5 transition-all duration-500 ${
                currentSlide === idx ? 'w-8 bg-[#10B981]' : 'w-4 bg-white/40'
              }`}
            />
          ))}
        </div>
        <button
          onClick={nextSlide}
          data-testid="hero-next-slide"
          className="w-12 h-12 flex items-center justify-center border border-white/30 hover:border-white text-white transition-all duration-300 hover:bg-white/10"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Scroll indicator */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 hidden sm:block"
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-1.5">
          <div className="w-1.5 h-3 bg-white/60 rounded-full" />
        </div>
      </motion.div>
    </section>
  );
}
